import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAdmin } from '../hooks/useAdmin';
import { maintenanceService, adminViewService } from '../services/maintenanceService';
import LoadingSpinner from './ui/LoadingSpinner';
import MaintenancePage from './MaintenancePage';
import CommunityTestingEndedPage from './CommunityTestingEndedPage';

export default function MaintenanceWrapper({ children }) {
  const { user } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [maintenanceStatus, setMaintenanceStatus] = useState({
    isMaintenanceMode: false,
    isCommunityTestingEnded: false,
    message: '',
    feedbackUrl: ''
  });
  const mainContentRef = useRef(null);

  useEffect(() => {
    loadMaintenanceStatus();
  }, []);

  // Wait for both maintenance status and admin status to load
  const isFullyLoaded = !loading && !adminLoading;

  // Debug loading states
  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 MaintenanceWrapper Loading States:', {
      maintenanceLoading: loading,
      adminLoading,
      isFullyLoaded,
      user: user ? `${user.username || user.attributes?.email} (${user.userId})` : 'null',
      isAdmin
    });
  }

  useEffect(() => {
    loadMaintenanceStatus();
  }, []);

  // Check for admin override URL parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const adminOverride = urlParams.get('admin');
    
    if (adminOverride === 'true' && isAdmin) {
      // Switch to admin mode and clean up URL
      adminViewService.setViewMode('admin');
      // Remove the parameter from URL without page reload
      const newUrl = new URL(window.location);
      newUrl.searchParams.delete('admin');
      window.history.replaceState({}, '', newUrl);
      
      // Announce the mode change to screen readers
      setStatusMessage('Admin mode activated. You now have access to administrative features.');
      
      // Trigger a re-render by updating maintenance status
      loadMaintenanceStatus();
    }
  }, [isAdmin]);

  // Focus management for state transitions
  useEffect(() => {
    if (isFullyLoaded && mainContentRef.current) {
      mainContentRef.current.focus();
    }
  }, [isFullyLoaded]);

  // Status announcements for screen readers
  useEffect(() => {
    if (isFullyLoaded) {
      if (maintenanceStatus.isMaintenanceMode && user) {
        if (isAdmin) {
          const adminViewMode = adminViewService.getViewMode();
          setStatusMessage(`Maintenance mode is active. You are viewing as ${adminViewMode === 'user' ? 'a regular user' : 'an administrator'}.`);
          document.title = `CertifiAble - Maintenance Mode (${adminViewMode === 'user' ? 'User View' : 'Admin View'})`;
        } else {
          setStatusMessage('Maintenance mode is active. The application is temporarily unavailable.');
          document.title = 'CertifiAble - Maintenance Mode';
        }
      } else if (maintenanceStatus.isCommunityTestingEnded && user) {
        if (isAdmin) {
          const adminViewMode = adminViewService.getViewMode();
          if (adminViewMode === 'user') {
            setStatusMessage('Community testing has ended. You are viewing the end-of-testing page as a regular user would see it.');
            document.title = 'CertifiAble - Community Testing Ended (User View)';
          } else {
            setStatusMessage('Application loaded successfully. Community testing has ended, but you have administrative access.');
            document.title = 'CertifiAble - Dashboard';
          }
        } else {
          setStatusMessage('Community testing has ended. Thank you for your participation.');
          document.title = 'CertifiAble - Community Testing Ended';
        }
      } else {
        setStatusMessage('Application loaded successfully');
        document.title = 'CertifiAble - Dashboard';
      }
    }
  }, [isFullyLoaded, maintenanceStatus.isMaintenanceMode, maintenanceStatus.isCommunityTestingEnded, user, isAdmin]);

  // Loading timeout for cognitive accessibility
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (loading || adminLoading) {
        console.warn('Maintenance status check timed out');
        setError('Unable to verify maintenance status within expected time. Please check your internet connection and try refreshing the page, or press R to retry.');
        setStatusMessage('Loading timed out - using default settings');
        setMaintenanceStatus({
          isMaintenanceMode: false,
          isCommunityTestingEnded: true,
          message: 'CertifiAble is currently under maintenance. Thank you for your patience.',
          feedbackUrl: 'https://docs.google.com/forms/d/e/1FAIpQLSffs0yIUCLLGDDyHR6zCEviKGS2M1V8TooQlK6_X-3w-0PjCw/viewform?usp=preview'
        });
        setLoading(false);
      }
    }, 10000); // 10 second timeout

    return () => clearTimeout(timeoutId);
  }, [loading, adminLoading]);

  // Keyboard shortcuts for accessibility
  useEffect(() => {
    const handleKeyPress = (event) => {
      // R key to retry loading when there's an error
      if (event.key === 'r' || event.key === 'R') {
        if (error && !loading && !adminLoading) {
          event.preventDefault();
          setStatusMessage('Retrying...');
          loadMaintenanceStatus();
        }
      }
    };

    if (error) {
      document.addEventListener('keydown', handleKeyPress);
      return () => document.removeEventListener('keydown', handleKeyPress);
    }
  }, [error, loading, adminLoading]);

  const loadMaintenanceStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const status = await maintenanceService.getMaintenanceStatus();
      setMaintenanceStatus(status);
    } catch (error) {
      console.error('Error loading maintenance status:', error);
      setError('Unable to check maintenance status. Using default settings. Press R to retry.');
      // Default to not in maintenance mode if we can't check
      // This ensures users can still access the application if the maintenance service fails
      setMaintenanceStatus({
        isMaintenanceMode: false,
        isCommunityTestingEnded: true,
        message: 'CertifiAble is currently under maintenance. Thank you for your patience.',
        feedbackUrl: 'https://docs.google.com/forms/d/e/1FAIpQLSffs0yIUCLLGDDyHR6zCEviKGS2M1V8TooQlK6_X-3w-0PjCw/viewform?usp=preview'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isFullyLoaded) {
    return (
      <>
        <div 
          ref={mainContentRef}
          className="flex items-center justify-center min-h-screen"
          role="main"
          aria-live="polite"
          aria-label="Loading application status"
          tabIndex="-1"
        >
          <LoadingSpinner size="lg" />
        </div>
        {/* Screen reader announcements */}
        <div 
          aria-live="polite" 
          aria-atomic="true" 
          className="sr-only"
        >
          {statusMessage}
        </div>
        {/* Error announcements */}
        {error && (
          <div 
            role="alert"
            aria-live="assertive"
            className="sr-only"
          >
            {error}
          </div>
        )}
      </>
    );
  }

  // If maintenance mode is OFF, but community testing ended is ON, show community testing ended page
  // BUT NOT for admin users (unless they're in user view mode)
  if (!maintenanceStatus.isMaintenanceMode && maintenanceStatus.isCommunityTestingEnded && user) {
    // Debug logging for troubleshooting
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 MaintenanceWrapper Debug:', {
        userObject: user,
        userEmail: user.username || user.email || user.attributes?.email,
        userId: user.userId,
        isAdmin,
        maintenanceMode: maintenanceStatus.isMaintenanceMode,
        communityTestingEnded: maintenanceStatus.isCommunityTestingEnded,
        adminViewMode: isAdmin ? adminViewService.getViewMode() : 'N/A',
        shouldShowCommunityTestingEnded: !maintenanceStatus.isMaintenanceMode && maintenanceStatus.isCommunityTestingEnded
      });
      
      // Extra debugging for the specific user
      if (user.username === 'frank.danielj@gmail.com' || user.attributes?.email === 'frank.danielj@gmail.com') {
        console.log('🚨 FRANK DEBUG - Should show Community Testing Ended page but might not be:', {
          userExists: !!user,
          isAdminValue: isAdmin,
          adminViewMode: isAdmin ? adminViewService.getViewMode() : 'N/A',
          willShowCommunityPage: !isAdmin || (isAdmin && adminViewService.getViewMode() === 'user')
        });
      }
    }
    
    // If user is admin, check their view mode first
    if (isAdmin) {
      const adminViewMode = adminViewService.getViewMode();
      // Only show community testing ended page if admin is in user view mode
      if (adminViewMode === 'user') {
        return (
          <CommunityTestingEndedPage 
            feedbackUrl={maintenanceStatus.feedbackUrl}
          />
        );
      }
      // Admin in admin/live mode should see normal app, continue to normal flow
    } else {
      // Regular user - show community testing ended page
      return (
        <CommunityTestingEndedPage 
          feedbackUrl={maintenanceStatus.feedbackUrl}
        />
      );
    }
  }

  // If maintenance mode is OFF, show normal application
  if (!maintenanceStatus.isMaintenanceMode) {
    return (
      <>
        <div ref={mainContentRef} tabIndex="-1" role="main" aria-label="Main application content">
          {children}
        </div>
        {/* Screen reader announcements */}
        <div 
          aria-live="polite" 
          aria-atomic="true" 
          className="sr-only"
        >
          {statusMessage}
        </div>
        {/* Error announcements */}
        {error && (
          <div 
            role="alert"
            aria-live="assertive"
            className="sr-only"
          >
            {error}
          </div>
        )}
      </>
    );
  }

  // If maintenance mode is ON, but user is not logged in - show normal application (home page, sign in, etc.)
  if (!user) {
    return (
      <>
        <div ref={mainContentRef} tabIndex="-1" role="main" aria-label="Main application content">
          {children}
        </div>
        {/* Screen reader announcements */}
        <div 
          aria-live="polite" 
          aria-atomic="true" 
          className="sr-only"
        >
          {statusMessage}
        </div>
        {/* Error announcements */}
        {error && (
          <div 
            role="alert"
            aria-live="assertive"
            className="sr-only"
          >
            {error}
          </div>
        )}
      </>
    );
  }

  // User is logged in and maintenance mode is ON
  if (!isAdmin) {
    // Regular user - show maintenance page
    return (
      <MaintenancePage 
        message={maintenanceStatus.message}
        feedbackUrl={maintenanceStatus.feedbackUrl}
        isAdmin={false}
        onRefresh={loadMaintenanceStatus}
      />
    );
  }

  // Admin user - check view mode
  const adminViewMode = adminViewService.getViewMode();
  
  // Check for admin escape route
  const currentPath = window.location.pathname;
  if (currentPath === '/admin-override' && isAdmin) {
    // Switch back to admin mode
    adminViewService.setViewMode('admin');
    // Redirect to admin dashboard
    window.location.href = '/admin';
    return null;
  }
  
  if (adminViewMode === 'user') {
    // Admin viewing as user - show maintenance page with admin controls
    return (
      <MaintenancePage 
        message={maintenanceStatus.message}
        feedbackUrl={maintenanceStatus.feedbackUrl}
        isAdmin={true}
        onRefresh={loadMaintenanceStatus}
      />
    );
  }

  // Admin in 'admin' or 'live' mode - show normal application
  return children;
}